import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { getSearchParamsFromEnergyDataAvailabilityPageState } from "../utils/getSearchParamsFromEnergyDataAvailabilityPageState";

export interface EnergyDataAvailabilityPageState {
  firstDate: DateTime<true>;
  lastDate: DateTime<true>;
  selectedSiteId?: string | null;
  searchTerm?: string;
}

const initialPageState: EnergyDataAvailabilityPageState = {
  firstDate: DateTime.now().startOf("year"),
  lastDate: DateTime.now().endOf("year"),
  selectedSiteId: undefined,
  searchTerm: undefined
};

export function useEnergyDataAvailabilityPageState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const validatedPageState = initialPageState;
  const pageStateKeys = Object.keys(validatedPageState);

  pageStateKeys.forEach((param) => {
    // on render, get search params & do safety constraint checks on them
    const searchParamValue = searchParams.get(param);

    // for any of the desired state props, do basic validation if there's a query param provided
    if (searchParamValue !== null) {
      // eslint-disable-next-line default-case
      switch (param) {
        case "firstDate": {
          const potentialFirstDate = DateTime.fromISO(searchParamValue);
          if (potentialFirstDate.isValid) {
            validatedPageState.firstDate = potentialFirstDate;
            validatedPageState.firstDate =
              validatedPageState.firstDate.startOf("month");
          }
          break;
        }

        case "lastDate": {
          const potentialLastDate = DateTime.fromISO(searchParamValue);
          if (potentialLastDate.isValid) {
            if (potentialLastDate > validatedPageState.firstDate) {
              validatedPageState.lastDate = potentialLastDate;
            } else {
              validatedPageState.lastDate = validatedPageState.firstDate.plus({
                days: 1
              });
            }
            validatedPageState.lastDate =
              validatedPageState.lastDate.endOf("month");
          }
          break;
        }

        case "selectedSiteId": {
          const potentialSelectedSiteId = searchParamValue;
          const potentialSiteIdAsNumber = Number(potentialSelectedSiteId);

          if (
            potentialSiteIdAsNumber &&
            !isNaN(potentialSiteIdAsNumber) &&
            potentialSiteIdAsNumber > 0
          ) {
            validatedPageState.selectedSiteId = potentialSelectedSiteId;
          }
          break;
        }

        case "searchTerm": {
          const potentialSearchTerm = searchParamValue;

          if (potentialSearchTerm !== "") {
            validatedPageState.searchTerm = potentialSearchTerm;
          }
          break;
        }
      }
    }
  });

  const [pageState, setPageState] =
    useState<EnergyDataAvailabilityPageState>(validatedPageState);

  useEffect(() => {
    const updatedSearchParams =
      getSearchParamsFromEnergyDataAvailabilityPageState(pageState);
    if (
      JSON.stringify(Object.fromEntries(searchParams)) !==
      JSON.stringify(Object.fromEntries(updatedSearchParams))
    ) {
      setSearchParams(updatedSearchParams, { replace: true });
    }
  }, [pageState, setSearchParams, searchParams]);

  return {
    pageState,
    setPageState
  };
}
